import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PassengerStatusEnum, SeatBlockState, SeatClass } from '@buspro/shared/dtos';
import {
  AuditsApi,
  AuditsPhotosApi,
  BookingSeatsDto,
  BusStopDto,
  CompletePassengerAuditDto,
  ConfirmEmptySeatDto,
  OperationAuditRecordsDto,
  OperationPhotosResponseDto,
  OperationsApi,
  OperationsDto,
  RecordUnbookedRiderDto,
  SeatPassengerDto,
  SubRouteDto,
} from '@buspro/web/apis';
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom } from 'rxjs';
import { AuditSeatPassengerStepperComponent } from '../../components/audit-seat-passenger-stepper/audit-seat-passenger-stepper.component';
import { SeatTaggedPhotoComponent } from '../../components/seat-tagged-photo/seat-tagged-photo.component';
import { type SeatTagPosition, parseAuditSeatTagLayout } from '../../utils/audit-seat-tag-layout.util';
import { legTitle as formatLegTitle } from './leg-title';

@Component({
  selector: 'buspro-admin-audit-detail-page',
  templateUrl: './audit-detail-page.component.html',
  styleUrls: ['./audit-detail-page.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    NzButtonModule,
    NzIconModule,
    NzModalModule,
    NzSelectModule,
    NzSpinModule,
    NzDividerModule,
    NzDropDownModule,
    NzMenuModule,
    NzEmptyModule,
    SeatTaggedPhotoComponent,
    AuditSeatPassengerStepperComponent,
  ],
})
export class AuditDetailPageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private auditsApi = inject(AuditsApi);
  private auditsPhotosApi = inject(AuditsPhotosApi);
  private operationsApi = inject(OperationsApi);
  private modal = inject(NzModalService);
  private notification = inject(NzNotificationService);
  private logger = inject(NGXLogger);
  private queryClient = injectQueryClient();

  operationId = signal<string | null>(null);

  private operationView = signal<AuditOperationViewModel | null>(null);
  // Seat is the TOP-LEVEL audit context now; selectedPassengerId below is the per-leg
  // sub-selection within the active seat.
  private selectedSeatId = signal<string | null>(null);
  private selectedPassengerId = signal<string | null>(null);
  private lastPassengerId = signal<string | null>(null);
  protected selectedBusStopId = signal<string | null>(null);

  // Seat-audit state, keyed by seat.id. Hydrated from `auditsControllerGetOperationAuditRecords`
  // on load and kept in sync by re-fetching the records query after every persist.
  private unbookedRidersBySeat = signal<Map<string, UnbookedRiderDraft[]>>(new Map());
  private confirmedEmptySeats = signal<Set<string>>(new Set());
  private readonly EMPTY_RIDERS: UnbookedRiderDraft[] = [];

  // Unbooked-rider inline form state (empty-seat anomaly capture)
  protected riderFormOpen = signal(false);
  protected riderOriginStopId = signal<string | null>(null);
  protected riderDropStopId = signal<string | null>(null);
  // When set, the form is editing an existing rider (by id) rather than adding a new one.
  protected editingRiderId = signal<string | null>(null);

  // Per-passenger audited start/end map — keyed by passenger.id
  private auditedStopsByPassenger = signal<Map<string, { originStopId: string | null; dropStopId: string | null }>>(
    new Map(),
  );

  // Which camera channel is shown BIG in the entry/exit galleries. null = follow the
  // passenger's channel (activeChannel). Set when the auditor clicks a thumbnail; reset
  // back to null whenever the passenger or the focused bus stop changes.
  private readonly entryBigChannel = signal<number | null>(null);
  private readonly exitBigChannel = signal<number | null>(null);
  private readonly lastPhotoContextKey = signal<string>('');

  // Which photo set the gallery shows (entry vs exit). Sticky across stops so an
  // auditor scanning exits stays on exit; shownDirection() falls back to whichever
  // direction actually has photos at the current stop.
  readonly galleryDirection = signal<'entry' | 'exit'>('entry');

  // Eye toggle: when on, the big image overlays EVERY seat tag on that channel
  // (with the current passenger's emphasized); when off, only the current seat.
  readonly showAllTags = signal<boolean>(false);

  private readonly operationQuery = injectQuery(() => ({
    queryKey: ['audit-operation-detail', this.operationId()],
    enabled: !!this.operationId(),
    queryFn: async () => {
      const id = this.operationId();
      if (!id) {
        throw new Error('Operation id is missing');
      }
      return lastValueFrom(this.operationsApi.operationsControllerGetById({ id }));
    },
    select: (operation: OperationsDto) => this.buildOperationView(operation),
    onError: (error: unknown) => {
      this.logger.error('Failed to load operation for audit detail:', error);
      this.notification.error(
        '❌ Failed to Load Operation',
        'The operation details could not be loaded. Please try again in a moment.',
      );
    },
  }));

  private readonly syncOperationView = effect(
    () => {
      const view = this.operationQuery.data();
      if (view) {
        this.operationView.set(view);
      }
    },
    { allowSignalWrites: true },
  );

  // Photos query
  private readonly photosQuery = injectQuery(() => ({
    queryKey: ['operation-photos', this.operationId()],
    enabled: !!this.operationId(),
    queryFn: async () => {
      const opId = this.operationId();
      if (!opId) throw new Error('Operation ID missing');
      return lastValueFrom(this.auditsPhotosApi.auditsPhotosControllerGetOperationPhotos({ operationId: opId }));
    },
    onError: (error: unknown) => {
      this.logger.error('Failed to load operation photos:', error);
    },
  }));

  photos = computed(() => (this.photosQuery.data() as OperationPhotosResponseDto | undefined)?.busStops ?? []);

  // Persisted seat-audit records (unbooked riders + confirmed-empty seats) used to hydrate the
  // seat panel on load. Invalidated after every persist so the screen reflects the server.
  private readonly auditRecordsQuery = injectQuery(() => ({
    queryKey: ['audit-records', this.operationId()],
    enabled: !!this.operationId(),
    queryFn: async () => {
      const id = this.operationId();
      if (!id) throw new Error('Operation ID missing');
      return lastValueFrom(this.auditsApi.auditsControllerGetOperationAuditRecords({ operationId: id }));
    },
    onError: (error: unknown) => {
      this.logger.error('Failed to load persisted audit records:', error);
    },
  }));

  private readonly syncAuditRecords = effect(
    () => {
      const records = this.auditRecordsQuery.data();
      // Depend on the view too so the seatNumber→seat fallback resolves once the operation loads.
      const view = this.operationView();
      if (records) {
        this.hydrateAuditRecords(records, view);
      }
    },
    { allowSignalWrites: true },
  );

  passengers = computed(() => (this.operationView()?.passengers ?? []).slice());
  busStops = computed(() => this.operationView()?.busStops ?? []);
  seats = computed(() => this.operationView()?.seats ?? []);
  operationData = computed(() => this.operationView()?.raw ?? null);
  seatGridColumns = computed(() => {
    const operation = this.operationData();
    const seats = this.seats();

    let columns = operation?.bus?.seatColumns ?? null;

    if (!columns || columns < 1) {
      columns = this.estimateSeatColumns(seats.length);
    }

    columns = Math.max(columns ?? 4, 1);

    return `repeat(${columns}, minmax(0, 1fr))`;
  });
  totalPassengers = computed(() => this.passengers().length);
  auditedPassengers = computed(() => this.passengers().filter((item) => item.passenger.audited).length);
  auditProgressPercent = computed(() => {
    const total = this.totalPassengers();
    if (!total) {
      return 0;
    }
    return Math.round((this.auditedPassengers() / total) * 100);
  });
  // The Complete Audit action is only offered once every passenger who used the bus
  // has been audited. passengers() is already filtered to used + auditable rows, but we
  // re-check isUsed explicitly so this stays correct if that upstream filter ever changes.
  allUsedPassengersAudited = computed(() => {
    const used = this.passengers().filter((item) => item.passenger.isUsed ?? true);
    return used.length > 0 && used.every((item) => item.passenger.audited);
  });
  // --- Seat-first context -------------------------------------------------
  // Every commercial seat (occupied OR empty) is auditable. Driver/conductor/turn-man/
  // removed blocks are excluded.
  auditableSeats = computed(() => this.seats().filter((s) => s.state === SeatBlockState.PASSENGER_SEAT));

  // The seat under audit. Falls back to the first not-yet-done seat, then the first seat.
  activeSeat = computed<BookingSeatsDto | null>(() => {
    const seats = this.auditableSeats();
    if (seats.length === 0) {
      return null;
    }
    const selectedId = this.selectedSeatId();
    if (selectedId) {
      const existing = seats.find((s) => s.id === selectedId);
      if (existing) {
        return existing;
      }
    }
    return seats.find((s) => !this.isSeatDone(s)) ?? seats[0] ?? null;
  });

  // The active passenger is now the selected LEG within the active seat (null when the
  // seat is empty). Defaults to the first un-audited leg of the seat.
  activePassenger = computed(() => {
    const seat = this.activeSeat();
    if (!seat) {
      return null;
    }
    const legs = this.seatPassengers(seat);
    if (legs.length === 0) {
      return null;
    }
    const selectedId = this.selectedPassengerId();
    if (selectedId) {
      const existing = legs.find((item) => item.passenger.id === selectedId);
      if (existing) {
        return existing;
      }
    }
    return legs.find((item) => !item.passenger.audited) ?? legs[0] ?? null;
  });

  // --- Seat-based progress (replaces the passenger-based bar) ---
  totalAuditableSeats = computed(() => this.auditableSeats().length);
  auditedSeatsCount = computed(() => this.auditableSeats().filter((s) => this.isSeatDone(s)).length);
  seatProgressPercent = computed(() => {
    const total = this.totalAuditableSeats();
    if (!total) {
      return 0;
    }
    return Math.round((this.auditedSeatsCount() / total) * 100);
  });
  // Complete Audit is offered once every commercial seat has been accounted for.
  allSeatsDone = computed(() => {
    const seats = this.auditableSeats();
    return seats.length > 0 && seats.every((s) => this.isSeatDone(s));
  });

  // --- Active-seat helpers (feed the slim stepper + booking overview) ---
  activeSeatPassengers = computed(() => {
    const seat = this.activeSeat();
    return seat ? this.seatPassengers(seat) : [];
  });
  activeSeatIsEmpty = computed(() => this.activeSeatPassengers().length === 0);
  activeLegOrdinal = computed(() => {
    const legs = this.activeSeatPassengers();
    const id = this.activePassenger()?.passenger.id ?? null;
    const idx = legs.findIndex((l) => l.passenger.id === id);
    return idx >= 0 ? idx + 1 : 1;
  });
  activeSeatLabel = computed(() => {
    const seat = this.activeSeat();
    if (!seat) {
      return '';
    }
    return seat.nickName ? `Seat ${seat.seatNumber} · ${seat.nickName}` : `Seat ${seat.seatNumber}`;
  });
  unbookedRidersForActiveSeat = computed(() => {
    const seat = this.activeSeat();
    return seat ? (this.unbookedRidersBySeat().get(seat.id) ?? this.EMPTY_RIDERS) : this.EMPTY_RIDERS;
  });
  confirmedEmptyActiveSeat = computed(() => {
    const seat = this.activeSeat();
    return !!seat && this.confirmedEmptySeats().has(seat.id);
  });

  // Live evaded-fare estimate for the unbooked-rider modal.
  protected riderEvadedFare = computed(() => {
    const view = this.operationView();
    const seat = this.activeSeat();
    const originStopId = this.riderOriginStopId();
    const dropStopId = this.riderDropStopId();
    if (!view || !seat || !originStopId || !dropStopId) {
      return null;
    }
    return this.estimateEvadedFare(seat, originStopId, dropStopId, view);
  });

  currentPassengerIndex = computed(() => {
    const passengers = this.passengers();
    const active = this.activePassenger();

    if (!active) {
      return -1;
    }

    return passengers.findIndex((item) => item.passenger.id === active.passenger.id);
  });

  // Resolved audited stops for the active passenger; falls back to booked origin / destination
  // The PERSISTED audited stops — only meaningful once the passenger is audited. Origin lives on
  // auditedOriginLocationId, drop on droppedLocationId (both bus-stop ids). Fall back to the booked
  // stops so the baseline is always defined.
  private persistedAuditedOriginStopId = computed(() => {
    const p = this.activePassenger();
    if (!p) return null;
    return (p.passenger.audited ? p.passenger.auditedOriginLocationId : null) ?? p.originStop?.id ?? null;
  });
  private persistedAuditedDropStopId = computed(() => {
    const p = this.activePassenger();
    if (!p) return null;
    return (p.passenger.audited ? p.passenger.droppedLocationId : null) ?? p.destinationStop?.id ?? null;
  });

  // The CURRENT audited selection: a this-session edit (auditedStopsByPassenger) wins, otherwise
  // the persisted/booked baseline above. This makes a revisited audited passenger show its real
  // saved stops instead of the booked ones.
  protected auditedOriginStopId = computed(() => {
    const p = this.activePassenger();
    if (!p) return null;
    return this.auditedStopsByPassenger().get(p.passenger.id)?.originStopId ?? this.persistedAuditedOriginStopId();
  });

  protected auditedDropStopId = computed(() => {
    const p = this.activePassenger();
    if (!p) return null;
    return this.auditedStopsByPassenger().get(p.passenger.id)?.dropStopId ?? this.persistedAuditedDropStopId();
  });

  // A previously-audited passenger is "dirty" when the current audited stops differ from what was
  // persisted; an un-audited passenger is always pending its first save.
  hasUnsavedAuditChanges = computed(() => {
    const p = this.activePassenger();
    if (!p) return false;
    if (!p.passenger.audited) return true;
    return (
      this.auditedOriginStopId() !== this.persistedAuditedOriginStopId() ||
      this.auditedDropStopId() !== this.persistedAuditedDropStopId()
    );
  });

  // Status chip for the booking-overview header.
  activeStatusChip = computed<{ label: string; pending: boolean; icon: string }>(() => {
    const p = this.activePassenger();
    if (p?.passenger.audited && this.hasUnsavedAuditChanges()) {
      return { label: 'Unsaved edit', pending: true, icon: 'fa-pencil' };
    }
    if (p?.passenger.audited) {
      return { label: 'Audited', pending: false, icon: 'fa-check-circle' };
    }
    return { label: 'Pending', pending: true, icon: 'fa-clock-o' };
  });

  fareDifference = computed(() => {
    const view = this.operationView();
    const passenger = this.activePassenger();
    const originStopId = this.auditedOriginStopId();
    const dropStopId = this.auditedDropStopId();

    if (!view || !passenger || !dropStopId || !originStopId) {
      return null;
    }

    return this.calculateFareDifference(passenger, originStopId, dropStopId, view);
  });

  // Seat-tag layout from bus.auditSeatTagsConfig (v2 only)
  seatTagLayout = computed(() => parseAuditSeatTagLayout(this.operationData()?.bus?.auditSeatTagsConfig ?? null));

  activeSeatTag = computed(() => {
    // Keyed off the ACTIVE SEAT (not the passenger) so the camera/tag still resolves on an
    // EMPTY seat. Tags are keyed by the COMMERCIAL seat number — the audit-tags config seeds
    // each tag's text as (seat.nickName ? seat.nickName : seatNumber), which matches the
    // "Seat N" shown to the auditor and what seat_passenger.seatNumber records.
    const seat = this.activeSeat();
    if (!seat) return null;
    const key = seat.nickName || String(seat.seatNumber);
    return this.seatTagLayout().seats.get(String(key).trim()) ?? null;
  });

  activeChannel = computed(() => this.activeSeatTag()?.channel ?? null);
  hasValidLayout = computed(() => !this.seatTagLayout().isLegacy && this.seatTagLayout().seats.size > 0);
  isLegacyTagConfig = computed(() => this.seatTagLayout().isLegacy);

  // Keep selectedSeatId pointing at a valid auditable seat; default to the first not-done seat.
  private readonly ensureSeatSelection = effect(
    () => {
      const seats = this.auditableSeats();

      if (seats.length === 0) {
        if (this.selectedSeatId()) {
          this.selectedSeatId.set(null);
        }
        return;
      }

      const selectedId = this.selectedSeatId();
      const exists = selectedId ? seats.some((s) => s.id === selectedId) : false;

      if (!exists) {
        const fallback = seats.find((s) => !this.isSeatDone(s)) ?? seats[0];
        this.selectedSeatId.set(fallback.id);
      }
    },
    { allowSignalWrites: true },
  );

  // Focus a sensible bus stop for photo viewing whenever the seat/leg context changes:
  // an occupied leg focuses its booked destination; an EMPTY seat focuses the first stop
  // so the auditor immediately sees footage to scan for an unbooked rider.
  private readonly syncFocusedStop = effect(
    () => {
      const seat = this.activeSeat();
      const passenger = this.activePassenger();
      const contextId = `${seat?.id ?? ''}::${passenger?.passenger.id ?? ''}`;
      const lastId = this.lastPassengerId();

      if (!seat) {
        this.lastPassengerId.set(null);
        this.selectedBusStopId.set(null);
        return;
      }

      if (contextId !== lastId) {
        this.lastPassengerId.set(contextId);
        const focusStopId = passenger?.destinationStop?.id ?? this.busStops()[0]?.id ?? null;
        this.selectedBusStopId.set(focusStopId);
      }
    },
    { allowSignalWrites: true },
  );

  // Reset the big-image selection back to the seat's own channel whenever the active seat
  // or the focused bus stop changes, so the big photo always defaults to "where this seat is"
  // before the auditor clicks around the thumbnails.
  private readonly resetBigPhotoSelection = effect(
    () => {
      const key = `${this.activeSeat()?.id ?? ''}::${this.selectedBusStopId() ?? ''}`;
      if (key !== this.lastPhotoContextKey()) {
        this.lastPhotoContextKey.set(key);
        this.entryBigChannel.set(null);
        this.exitBigChannel.set(null);
      }
    },
    { allowSignalWrites: true },
  );

  // Mutation for completing audit
  completeAuditMutation = injectMutation(() => ({
    mutationFn: (operationId: string) =>
      lastValueFrom(this.operationsApi.operationsControllerCompleteAudit({ id: operationId })),
    onSuccess: () => {
      this.notification.success('✅ Audit Completed', 'The audit has been marked as completed.');
      // Invalidate all audit-related queries to refresh badges and lists
      this.queryClient.invalidateQueries({ queryKey: ['sidebar-current-audit-badge'] });
      this.queryClient.invalidateQueries({ queryKey: ['sidebar-ready-for-audit-badge'] });
      this.queryClient.invalidateQueries({ queryKey: ['my-current-audit'] });
      this.queryClient.invalidateQueries({ queryKey: ['my-audit-history'] });
      this.queryClient.invalidateQueries({ queryKey: ['my-audit-stats'] });
      setTimeout(() => {
        this.router.navigate(['/audits/my-audits']);
      }, 500);
    },
    onError: (error: unknown) => {
      this.logger.error('Failed to complete audit:', error);
      this.notification.error('❌ Failed to Complete Audit', 'An error occurred while completing the audit.');
    },
  }));

  // Mutation for cancelling audit
  cancelAuditMutation = injectMutation(() => ({
    mutationFn: (operationId: string) =>
      lastValueFrom(this.operationsApi.operationsControllerCancelAudit({ id: operationId })),
    onSuccess: () => {
      this.notification.success('✅ Audit Cancelled', 'The audit has been cancelled and returned to the queue.');
      // Invalidate all audit-related queries to refresh badges and lists
      this.queryClient.invalidateQueries({ queryKey: ['sidebar-current-audit-badge'] });
      this.queryClient.invalidateQueries({ queryKey: ['sidebar-ready-for-audit-badge'] });
      this.queryClient.invalidateQueries({ queryKey: ['my-current-audit'] });
      this.queryClient.invalidateQueries({ queryKey: ['operations-ready-for-audit'] });
      setTimeout(() => {
        this.router.navigate(['/audits/ready']);
      }, 500);
    },
    onError: (error: unknown) => {
      this.logger.error('Failed to cancel audit:', error);
      this.notification.error('❌ Failed to Cancel Audit', 'An error occurred while cancelling the audit.');
    },
  }));

  markPassengerAuditedMutation = injectMutation(() => ({
    mutationFn: (payload: CompletePassengerAuditMutationPayload) =>
      lastValueFrom(
        this.auditsApi.auditsControllerCompletePassengerAudit({
          passengerId: payload.passengerId,
          body: payload.body,
        }),
      ),
    onSuccess: (_data, variables) => {
      // Capture the name BEFORE advancing (advanceToNextUnaudited swaps the active passenger).
      const auditedName = this.activePassenger()?.passenger.customerName || 'Passenger';
      const isCorrection = variables.wasAlreadyAudited;
      this.notification.success(
        isCorrection ? '✅ Audit Updated' : '✅ Passenger Audited',
        `${auditedName} audit ${isCorrection ? 'updated' : 'recorded'} successfully.`,
      );
      // Save on a first audit flows to the next un-audited passenger; corrections and
      // mark-as-start/end instant-persists stay put for review.
      if (variables.advance) {
        this.advanceToNextUnaudited();
      }
      this.operationQuery.refetch();
      this.queryClient.invalidateQueries({ queryKey: ['sidebar-current-audit-badge'] });
      this.queryClient.invalidateQueries({ queryKey: ['sidebar-ready-for-audit-badge'] });
    },
    onError: (error: unknown) => {
      this.logger.error('Failed to mark passenger as audited:', error);
      this.notification.error(
        '❌ Failed to Audit Passenger',
        'We could not save the passenger audit. Please try again shortly.',
      );
    },
  }));

  // --- Unbooked-rider + confirm-empty persistence -------------------------------
  // Each mutation hits the new audit-records endpoints and then refetches the records
  // query so `unbookedRidersBySeat` / `confirmedEmptySeats` re-hydrate from the server.
  saveUnbookedRiderMutation = injectMutation(() => ({
    mutationFn: (vars: { operationId: string; riderId: string | null; body: RecordUnbookedRiderDto }) =>
      vars.riderId
        ? lastValueFrom(
            this.auditsApi.auditsControllerUpdateUnbookedRider({
              operationId: vars.operationId,
              riderId: vars.riderId,
              body: vars.body,
            }),
          )
        : lastValueFrom(
            this.auditsApi.auditsControllerRecordUnbookedRider({
              operationId: vars.operationId,
              body: vars.body,
            }),
          ),
    onSuccess: () => {
      this.refreshAuditRecords();
    },
    onError: (error: unknown) => {
      this.logger.error('Failed to save unbooked rider:', error);
      this.notification.error(
        '❌ Failed to Save Rider',
        'We could not save the unbooked rider. Please try again shortly.',
      );
    },
  }));

  deleteUnbookedRiderMutation = injectMutation(() => ({
    mutationFn: (vars: { operationId: string; riderId: string }) =>
      lastValueFrom(
        this.auditsApi.auditsControllerDeleteUnbookedRider({
          operationId: vars.operationId,
          riderId: vars.riderId,
        }),
      ),
    onSuccess: () => {
      this.refreshAuditRecords();
    },
    onError: (error: unknown) => {
      this.logger.error('Failed to remove unbooked rider:', error);
      this.notification.error(
        '❌ Failed to Remove Rider',
        'We could not remove the unbooked rider. Please try again shortly.',
      );
    },
  }));

  confirmEmptySeatMutation = injectMutation(() => ({
    mutationFn: (vars: { operationId: string; body: ConfirmEmptySeatDto }) =>
      lastValueFrom(
        this.auditsApi.auditsControllerConfirmEmptySeat({
          operationId: vars.operationId,
          body: vars.body,
        }),
      ),
    onSuccess: () => {
      this.refreshAuditRecords();
    },
    onError: (error: unknown) => {
      this.logger.error('Failed to confirm empty seat:', error);
      this.notification.error(
        '❌ Failed to Confirm Seat',
        'We could not mark the seat as empty. Please try again shortly.',
      );
    },
  }));

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('operationId');
    if (id) {
      this.operationId.set(id);
      this.operationQuery.refetch();
    }
  }

  get isLoadingOperation() {
    return this.operationQuery.isPending();
  }

  get hasOperationError() {
    return this.operationQuery.isError();
  }

  get isNavigatingPassengersDisabled() {
    // Leg nav only matters when the seat carries more than one leg; also locked while a save
    // is in flight so manual nav can't race the onSuccess auto-advance.
    return this.activeSeatPassengers().length <= 1 || this.markPassengerAuditedMutation.isPending();
  }

  // Save stays locked through both the in-flight mutation AND the post-save refetch:
  // until the refetch lands, local data still shows audited=false so canSubmit would
  // otherwise re-enable Save and a second click could fire a duplicate audit POST.
  get isSavingPassengerAudit() {
    return this.markPassengerAuditedMutation.isPending() || this.operationQuery.isFetching();
  }

  retryOperationLoad() {
    this.operationQuery.refetch();
  }

  completeAudit() {
    const opId = this.operationId();
    if (!opId) return;

    this.modal.confirm({
      nzTitle: 'Complete Audit?',
      nzContent: 'Are you sure you want to mark this audit as completed?',
      nzOkText: 'Yes, Complete',
      nzOkType: 'primary',
      nzOnOk: () => {
        this.completeAuditMutation.mutate(opId);
      },
    });
  }

  cancelAudit() {
    const opId = this.operationId();
    if (!opId) return;

    this.modal.confirm({
      nzTitle: 'Cancel Audit?',
      nzContent:
        'Are you sure you want to cancel this audit? The operation will be returned to the queue for others to audit.',
      nzOkText: 'Yes, Cancel Audit',
      nzOkDanger: true,
      nzOnOk: () => {
        this.cancelAuditMutation.mutate(opId);
      },
    });
  }

  canSubmitPassengerAudit() {
    const passenger = this.activePassenger();
    const dropStopId = this.auditedDropStopId();
    const originStopId = this.auditedOriginStopId();
    if (!passenger || !dropStopId || !originStopId) {
      return false;
    }
    // Enabled for a first audit, OR when an already-audited passenger has edited stops (correction).
    return this.hasUnsavedAuditChanges();
  }

  // `options.advance` overrides the default advance behavior (first audits advance, corrections
  // stay). `options.silent` suppresses the "missing data" warning for the instant-persist path,
  // which only fires when both stops are already present.
  markPassengerAsAudited(options?: { advance?: boolean; silent?: boolean }) {
    if (this.markPassengerAuditedMutation.isPending()) {
      return;
    }

    const view = this.operationView();
    const passengerDetail = this.activePassenger();
    const dropStopId = this.auditedDropStopId();
    const originStopId = this.auditedOriginStopId();

    if (!view || !passengerDetail || !dropStopId || !originStopId) {
      if (!options?.silent) {
        this.notification.warning(
          '⚠️ Missing Data',
          'Select a passenger and both audited start + end stops before submitting.',
        );
      }
      return;
    }

    const dropStop = view.busStopById.get(dropStopId);
    const originStop = view.busStopById.get(originStopId);
    if (!dropStop || !originStop) {
      this.notification.warning(
        '⚠️ Invalid Stop Selected',
        'The selected audited stop(s) could not be found in the operation route.',
      );
      return;
    }

    const bookingProfileId = passengerDetail.passenger.bookingProfileId ?? view.raw.bookingProfile?.id ?? null;
    const operationId = view.raw.id ?? this.operationId();

    if (!bookingProfileId || !operationId) {
      this.notification.error(
        '❌ Missing Operation Data',
        'The booking profile or operation could not be identified. Please reload and try again.',
      );
      return;
    }

    const fareDifference = this.fareDifference();
    const auditResult = this.resolveAuditResult(passengerDetail, dropStop, fareDifference);

    const payload: CompletePassengerAuditDto = {
      bookingProfileId,
      operationId,
      passengerId: passengerDetail.passenger.id,
      auditedDropStopId: dropStop.id,
      auditedDropStopName: dropStop.name,
      auditedDropStopOrderIndex: dropStop.orderIndex,
      auditedOriginStopId: originStop.id,
      auditedOriginStopName: originStop.name,
      auditedOriginOrderIndex: originStop.orderIndex,
      auditResult,
      auditorComment: undefined,
      monetaryValueLost: Math.max(fareDifference?.difference ?? 0, 0),
      fareDelta: fareDifference?.difference ?? 0,
      auditedFare: fareDifference?.auditedPrice ?? passengerDetail.bookedPrice,
      actualSubRouteId: fareDifference?.actualSubRoute?.id ?? passengerDetail.bookedSubRoute?.id ?? undefined,
      auditedSubRouteName: fareDifference?.actualSubRoute?.name ?? passengerDetail.bookedSubRoute?.name ?? undefined,
      // Booked snapshot so passenger_audits captures booked-vs-audited independent of the booking row.
      bookedOriginStopId: passengerDetail.originStop?.id ?? undefined,
      bookedOriginStopName: passengerDetail.originStop?.name ?? undefined,
      bookedOriginOrderIndex: passengerDetail.originStop?.orderIndex ?? undefined,
      bookedDropStopId: passengerDetail.destinationStop?.id ?? undefined,
      bookedDropStopName: passengerDetail.destinationStop?.name ?? undefined,
      bookedDropOrderIndex: passengerDetail.destinationStop?.orderIndex ?? undefined,
      detectedAt: new Date().toISOString(),
    };

    // Save directly (no confirm dialog) so the auditor flows quickly through the queue;
    // onSuccess advances to the next un-audited passenger (first audits via Save only).
    const wasAlreadyAudited = passengerDetail.passenger.audited;
    this.markPassengerAuditedMutation.mutate({
      passengerId: passengerDetail.passenger.id,
      body: payload,
      wasAlreadyAudited,
      advance: options?.advance ?? !wasAlreadyAudited,
    });
  }

  // Within-seat leg navigation (the right-panel Prev/Next + the slim stepper).
  navigatePassenger(direction: 'previous' | 'next') {
    const legs = this.activeSeatPassengers();
    if (legs.length === 0) {
      return;
    }
    const currentIndex = legs.findIndex((l) => l.passenger.id === this.activePassenger()?.passenger.id);
    const offset = direction === 'next' ? 1 : -1;
    const nextIndex = (Math.max(currentIndex, 0) + offset + legs.length) % legs.length;
    this.selectedPassengerId.set(legs[nextIndex].passenger.id);
  }

  // Seat-level navigation across all auditable seats (wraps). Favors progress: skips seats that
  // are already audited / confirmed-empty / accounted-for, landing on the next (or previous)
  // seat that still needs attention. When every other seat is done it falls back to a plain
  // linear step so a completed trip can still be reviewed (and the seat-map grid always allows
  // jumping directly to any seat).
  navigateSeat(direction: 'previous' | 'next') {
    const seats = this.auditableSeats();
    const n = seats.length;
    if (n === 0) {
      return;
    }
    const step = direction === 'next' ? 1 : -1;
    const currentId = this.activeSeat()?.id ?? null;
    const start = Math.max(
      seats.findIndex((s) => s.id === currentId),
      0,
    );
    // First pass: nearest not-done seat in the chosen direction.
    for (let i = 1; i <= n; i++) {
      const candidate = seats[(((start + step * i) % n) + n) % n];
      if (candidate.id !== currentId && !this.isSeatDone(candidate)) {
        this.focusSeat(candidate);
        return;
      }
    }
    // Everything else is done — plain linear step for review.
    this.focusSeat(seats[(((start + step) % n) + n) % n]);
  }

  // After a successful save: advance to the next un-audited leg in the SAME seat; if the
  // seat is exhausted, jump to the next not-done seat. Skips the just-saved leg (its audited
  // flag hasn't refreshed locally until the refetch lands).
  private advanceToNextUnaudited() {
    const seat = this.activeSeat();
    const currentId = this.activePassenger()?.passenger.id ?? null;
    if (seat) {
      const legs = this.seatPassengers(seat);
      const remaining = legs.find((l) => l.passenger.id !== currentId && !l.passenger.audited);
      if (remaining) {
        this.selectedPassengerId.set(remaining.passenger.id);
        return;
      }
    }
    this.navigateToNextNotDoneSeat();
  }

  // Find the next seat (forward, wrapping) that still needs attention and focus it. Skips the
  // current seat so a just-completed seat doesn't re-select itself before the refetch lands.
  private navigateToNextNotDoneSeat() {
    const seats = this.auditableSeats();
    if (seats.length === 0) {
      return;
    }
    const currentSeatId = this.activeSeat()?.id ?? null;
    const start = Math.max(
      seats.findIndex((s) => s.id === currentSeatId),
      0,
    );
    for (let i = 1; i <= seats.length; i++) {
      const candidate = seats[(start + i) % seats.length];
      if (candidate && candidate.id !== currentSeatId && !this.isSeatDone(candidate)) {
        this.focusSeat(candidate);
        return;
      }
    }
  }

  // Focus a seat and default its leg to the first un-audited passenger (null when empty).
  private focusSeat(seat: BookingSeatsDto) {
    this.selectedSeatId.set(seat.id);
    const legs = this.seatPassengers(seat);
    const target = legs.find((l) => !l.passenger.audited) ?? legs[0] ?? null;
    this.selectedPassengerId.set(target ? target.passenger.id : null);
  }

  selectPassenger(passengerId: string) {
    this.selectedPassengerId.set(passengerId);
  }

  // Hover label for a leg rail node. Shared with the passenger bar so both name a leg alike.
  legTitle(leg: PassengerAuditViewModel): string {
    return formatLegTitle(leg);
  }

  // Pause = step away; per-passenger progress is already saved server-side.
  pauseAudit() {
    this.router.navigate(['/audits/my-audits']);
  }

  // Keyboard shortcuts for a fast audit queue:
  //   ← / →  = previous / next SEAT (outer loop)
  //   [ / ]  = previous / next LEG within the seat (inner loop)
  //   Enter  = Save the active passenger audit
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    const target = event.target as HTMLElement | null;
    const typing =
      !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
    // Don't hijack keys while typing or while the inline unbooked-rider form is open.
    if (typing || this.riderFormOpen()) {
      return;
    }
    switch (event.key) {
      case 'ArrowLeft':
        this.navigateSeat('previous');
        event.preventDefault();
        break;
      case 'ArrowRight':
        this.navigateSeat('next');
        event.preventDefault();
        break;
      case '[':
        this.navigatePassenger('previous');
        event.preventDefault();
        break;
      case ']':
        this.navigatePassenger('next');
        event.preventDefault();
        break;
      case 'Enter':
        // Let a focused button/link handle its own Enter; otherwise Save.
        if (target && (target.tagName === 'BUTTON' || target.tagName === 'A')) {
          return;
        }
        if (this.canSubmitPassengerAudit() && !this.isSavingPassengerAudit) {
          this.markPassengerAsAudited();
          event.preventDefault();
        }
        break;
      default:
        break;
    }
  }

  selectBusStop(stopId: string) {
    this.selectedBusStopId.set(stopId);
  }

  // Route order of a stop (by orderIndex) — the basis for the directional start<end rule.
  private stopOrderIndex(stopId: string | null): number | null {
    if (!stopId) return null;
    return this.operationView()?.busStopById.get(stopId)?.orderIndex ?? null;
  }

  // A stop may be marked START only if it is strictly before the current audited END on the route,
  // and END only if strictly after the current audited START. Equality (same stop) is disallowed.
  canMarkAsStart(stop: BusStopDto): boolean {
    const end = this.stopOrderIndex(this.auditedDropStopId());
    return end === null ? true : stop.orderIndex < end;
  }

  canMarkAsEnd(stop: BusStopDto): boolean {
    const start = this.stopOrderIndex(this.auditedOriginStopId());
    return start === null ? true : stop.orderIndex > start;
  }

  markStopAsStart(stopId: string) {
    const end = this.stopOrderIndex(this.auditedDropStopId());
    const start = this.stopOrderIndex(stopId);
    if (end !== null && start !== null && start >= end) {
      this.notification.warning('⚠️ Invalid Start', 'The start stop must come before the end stop on the route.');
      return;
    }
    this.setAuditedStop('originStopId', stopId);
  }

  markStopAsEnd(stopId: string) {
    const start = this.stopOrderIndex(this.auditedOriginStopId());
    const end = this.stopOrderIndex(stopId);
    if (start !== null && end !== null && end <= start) {
      this.notification.warning('⚠️ Invalid End', 'The end stop must come after the start stop on the route.');
      return;
    }
    this.setAuditedStop('dropStopId', stopId);
  }

  private setAuditedStop(field: 'originStopId' | 'dropStopId', stopId: string) {
    const passenger = this.activePassenger();
    if (!passenger) return;
    const next = new Map(this.auditedStopsByPassenger());
    const prev = next.get(passenger.passenger.id) ?? { originStopId: null, dropStopId: null };
    next.set(passenger.passenger.id, { ...prev, [field]: stopId });
    this.auditedStopsByPassenger.set(next);
    this.selectedBusStopId.set(stopId);

    // Instant-persist: once BOTH audited stops are present, marking a stop saves the audit
    // straight away (no Save click needed) but never auto-advances — the auditor stays on the
    // passenger. Save remains the explicit "confirm & next" action. Skips while a save is in
    // flight; the dirty "Unsaved edit" chip + Save then cover any mark that raced the request.
    if (this.auditedOriginStopId() && this.auditedDropStopId() && !this.markPassengerAuditedMutation.isPending()) {
      this.markPassengerAsAudited({ advance: false, silent: true });
    }
  }

  // Look up the photo for the active passenger's channel at a given bus stop.
  // Returns a SeatTaggedPhotoInput-shaped object (or null) — the URL field on
  // OperationPhotoDto is typed as object|null in the generated client but is
  // actually a string at runtime, so we coerce here.
  entryPhotoForActive(busStopId: string) {
    return this.toTaggedPhotoInput(this.findPhotoOnChannel(this.getEntryPhotos(busStopId)));
  }

  exitPhotoForActive(busStopId: string) {
    return this.toTaggedPhotoInput(this.findPhotoOnChannel(this.getExitPhotos(busStopId)));
  }

  private findPhotoOnChannel<T extends { channel: number }>(photos: T[]): T | null {
    const ch = this.activeChannel();
    if (!ch) return null;
    return photos.find((p) => p.channel === ch) ?? null;
  }

  private toTaggedPhotoInput(
    photo: { url: unknown; channel: number; timestamp?: string | Date | null } | null,
  ): { url: string | null; channel: number | null; timestamp?: string | Date | null } | null {
    if (!photo) return null;
    return {
      url: typeof photo.url === 'string' ? photo.url : photo.url == null ? null : String(photo.url),
      channel: photo.channel,
      timestamp: photo.timestamp ?? null,
    };
  }

  // --- Entry/Exit snapshot galleries: one BIG image (the passenger's channel by default)
  //     + a clickable thumbnail strip of every channel captured at the stop. ---

  // All channel photos at a stop, sorted by channel for a stable thumbnail order.
  entrySnapshots(busStopId: string) {
    return [...this.getEntryPhotos(busStopId)].sort((a, b) => (a.channel ?? 0) - (b.channel ?? 0));
  }

  exitSnapshots(busStopId: string) {
    return [...this.getExitPhotos(busStopId)].sort((a, b) => (a.channel ?? 0) - (b.channel ?? 0));
  }

  // The photo shown big: the explicitly-selected channel, else the passenger's channel,
  // else the first available — coerced to the seat-tagged-photo input shape.
  private resolveBigPhoto<T extends { channel: number; url: unknown; timestamp?: string | Date | null }>(
    photos: T[],
    override: number | null,
  ) {
    if (photos.length === 0) return null;
    const wantChannel = override ?? this.activeChannel();
    const match = wantChannel != null ? photos.find((p) => p.channel === wantChannel) : null;
    return this.toTaggedPhotoInput(match ?? photos[0]);
  }

  entryBigPhoto(busStopId: string) {
    return this.resolveBigPhoto(this.entrySnapshots(busStopId), this.entryBigChannel());
  }

  exitBigPhoto(busStopId: string) {
    return this.resolveBigPhoto(this.exitSnapshots(busStopId), this.exitBigChannel());
  }

  toggleAllTags() {
    this.showAllTags.update((v) => !v);
  }

  // Tags to overlay on the entry/exit big image: every seat on the displayed
  // channel when "show all" is on, else just the current passenger's seat (and
  // only when the displayed channel is actually the passenger's channel).
  entryBigTags(busStopId: string): SeatTagPosition[] {
    return this.bigTags(this.entryBigPhoto(busStopId)?.channel ?? null);
  }

  exitBigTags(busStopId: string): SeatTagPosition[] {
    return this.bigTags(this.exitBigPhoto(busStopId)?.channel ?? null);
  }

  private bigTags(channel: number | null): SeatTagPosition[] {
    if (channel == null) return [];
    if (this.showAllTags()) {
      return [...this.seatTagLayout().seats.values()].filter((t) => t.channel === channel);
    }
    const active = this.activeSeatTag();
    return active && active.channel === channel ? [active] : [];
  }

  selectEntryBig(channel: number | null) {
    this.entryBigChannel.set(channel);
  }

  selectExitBig(channel: number | null) {
    this.exitBigChannel.set(channel);
  }

  // The direction actually rendered: the toggle's pick, but auto-falling back to
  // the other direction when the picked one has no photos at the current stop.
  shownDirection = computed<'entry' | 'exit'>(() => {
    const stopId = this.selectedBusStopId();
    const want = this.galleryDirection();
    if (!stopId) return want;
    const hasEntry = this.entrySnapshots(stopId).length > 0;
    const hasExit = this.exitSnapshots(stopId).length > 0;
    if (want === 'entry' && !hasEntry && hasExit) return 'exit';
    if (want === 'exit' && !hasExit && hasEntry) return 'entry';
    return want;
  });

  setGalleryDirection(direction: 'entry' | 'exit') {
    this.galleryDirection.set(direction);
  }

  // Helper for the right-panel labels — converts a stop id into a human name
  stopNameById(stopId: string | null): string {
    if (!stopId) return '—';
    const view = this.operationView();
    if (!view) return '—';
    return view.busStopById.get(stopId)?.name ?? '—';
  }

  // Tells the template whether the audited origin / drop differ from booked
  auditedOriginChanged = computed(() => {
    const p = this.activePassenger();
    if (!p) return false;
    return this.auditedOriginStopId() !== (p.originStop?.id ?? null);
  });

  auditedDropChanged = computed(() => {
    const p = this.activePassenger();
    if (!p) return false;
    return this.auditedDropStopId() !== (p.destinationStop?.id ?? null);
  });

  getSelectedBusStop() {
    const view = this.operationView();
    const stopId = this.selectedBusStopId();
    if (!view || !stopId) {
      return null;
    }

    return view.busStopById.get(stopId) ?? null;
  }

  getBusStopVisualState(stop: BusStopDto) {
    const passenger = this.activePassenger();
    if (!passenger) {
      return 'neutral';
    }

    const originOrder = passenger.originStop?.orderIndex ?? null;
    const destinationOrder = passenger.destinationStop?.orderIndex ?? null;

    if (originOrder === null || destinationOrder === null) {
      return 'neutral';
    }

    if (stop.id === this.auditedOriginStopId() || stop.id === this.auditedDropStopId()) {
      return 'selected';
    }

    if (stop.orderIndex >= originOrder && stop.orderIndex <= destinationOrder) {
      return 'booked-range';
    }

    if (stop.orderIndex > destinationOrder) {
      return 'beyond-range';
    }

    return 'trail';
  }

  // For START/END badge rendering on bus-stop pills
  isAuditedStart(stop: BusStopDto): boolean {
    return this.auditedOriginStopId() === stop.id;
  }

  isAuditedEnd(stop: BusStopDto): boolean {
    return this.auditedDropStopId() === stop.id;
  }

  // Persistent BOOKED markers (from the original ticket). They read the booked origin /
  // destination stops directly, so they NEVER move when the auditor re-marks the audited
  // start / end — letting the auditor always see where the ticket said the passenger
  // boarded and alighted.
  isBookedStart(stop: BusStopDto): boolean {
    return this.activePassenger()?.originStop?.id === stop.id;
  }

  isBookedEnd(stop: BusStopDto): boolean {
    return this.activePassenger()?.destinationStop?.id === stop.id;
  }

  isSeatHighlighted(seat: BookingSeatsDto) {
    return this.activeSeat()?.id === seat.id;
  }

  isSeatAudited(seat: BookingSeatsDto) {
    const view = this.operationView();
    if (!view) {
      return false;
    }

    const seatPassengers =
      view.passengersBySeatId.get(seat.id) ?? view.passengersBySeatNumber.get(seat.seatNumber) ?? [];

    return seatPassengers.some((item) => item.passenger.audited);
  }

  isSeatFullyAudited(seat: BookingSeatsDto) {
    const view = this.operationView();
    if (!view) {
      return false;
    }

    const seatPassengers =
      view.passengersBySeatId.get(seat.id) ?? view.passengersBySeatNumber.get(seat.seatNumber) ?? [];

    if (seatPassengers.length === 0) {
      return false;
    }

    return seatPassengers.every((item) => item.passenger.audited);
  }

  // A seat is "done" (counts toward seat progress) when: an occupied seat has every leg
  // audited, OR an empty seat has been confirmed empty OR has ≥1 recorded unbooked rider.
  isSeatDone(seat: BookingSeatsDto) {
    if (seat.state !== SeatBlockState.PASSENGER_SEAT) {
      return false;
    }
    const legs = this.seatPassengers(seat);
    if (legs.length > 0) {
      return legs.every((item) => item.passenger.audited);
    }
    return this.confirmedEmptySeats().has(seat.id) || (this.unbookedRidersBySeat().get(seat.id)?.length ?? 0) > 0;
  }

  hasUnbookedRiders(seat: BookingSeatsDto) {
    return (this.unbookedRidersBySeat().get(seat.id)?.length ?? 0) > 0;
  }

  getSeatOccupancyStatus(seat: BookingSeatsDto) {
    const view = this.operationView();
    if (!view) {
      return 'empty';
    }

    const seatPassengers =
      view.passengersBySeatId.get(seat.id) ?? view.passengersBySeatNumber.get(seat.seatNumber) ?? [];

    if (seatPassengers.length === 0) {
      return 'empty';
    }

    if (seatPassengers.some((passenger) => passenger.passenger.status === PassengerStatusEnum.Dropped)) {
      return 'occupied';
    }

    return 'reserved';
  }

  // --- Seat click → jump to a passenger of that seat ---
  // A seat may carry several passengers across the trip (segmented usage: A→B, B→C, …).
  // Clicking a commercial passenger seat focuses an UN-audited passenger of that seat if any
  // remain, otherwise the first. Driver/conductor/turn-man and empty seats are inert.
  private seatPassengers(seat: BookingSeatsDto): PassengerAuditViewModel[] {
    const view = this.operationView();
    if (!view) return [];
    return view.passengersBySeatId.get(seat.id) ?? view.passengersBySeatNumber.get(seat.seatNumber) ?? [];
  }

  // Every commercial seat is selectable now (empty seats are the whole point of seat-first
  // auditing); driver/conductor/turn-man/removed blocks stay inert.
  isSeatSelectable(seat: BookingSeatsDto): boolean {
    return seat.state === SeatBlockState.PASSENGER_SEAT;
  }

  selectSeat(seat: BookingSeatsDto) {
    // Don't race the post-save auto-advance, and ignore special / removed blocks.
    if (this.markPassengerAuditedMutation.isPending() || !this.isSeatSelectable(seat)) {
      return;
    }
    this.focusSeat(seat);
  }

  /**
   * Was this seat SOLD end to end, or only in pieces?
   *
   * "Partial" means no single booking on the seat runs the route's start terminal to its end —
   * either one passenger who boarded late or alighted early, or several riding it in relay.
   * Both are the same commercial fact: the seat was never sold as one through fare, so its
   * money reconciles leg by leg, and the auditor should see that before clicking in.
   *
   * Compared by TERMINAL ID, not by sub-route name and not by mapping terminals onto bus
   * stops. Names go stale when a place is renamed (this trip has sub-routes still reading
   * "Hagvan Office" on a route that starts at "Hagvan Enter"), and the stop mapping yields
   * null whenever a sub-route terminal is not among the route's stops — which would read as
   * "does not cover the route" and silently mark an end-to-end booking partial.
   *
   * An EMPTY seat is never partial: it was not used at all, and it already has its own
   * empty / confirm-empty treatment. Returns 0 for "not partial", else the number of legs,
   * which the styling turns into that many diagonal bands.
   */
  private seatPartialLegCount(seat: BookingSeatsDto): number {
    const legs = this.seatPassengers(seat);
    if (legs.length === 0) {
      return 0;
    }

    const route = this.operationView()?.raw?.route;
    if (!route?.startTerminalId || !route?.endTerminalId) {
      return 0;
    }

    const soldEndToEnd = legs.some(
      (leg) =>
        leg.bookedSubRoute?.fromTerminalId === route.startTerminalId &&
        leg.bookedSubRoute?.toTerminalId === route.endTerminalId,
    );

    return soldEndToEnd ? 0 : legs.length;
  }

  seatTooltip(seat: BookingSeatsDto): string {
    // Only commercial passenger seats (gold/silver/bronze) get a tooltip; layout/special
    // blocks (spacers, driver, conductor, turn-man) are ignored.
    if (seat.state !== SeatBlockState.PASSENGER_SEAT) {
      return '';
    }
    const seatPassengers = this.seatPassengers(seat);
    const seatLabel = `Seat ${seat.seatNumber}`;
    if (seatPassengers.length === 0) {
      return `${seatLabel} · empty (no passengers this trip)`;
    }
    const auditedCount = seatPassengers.filter((item) => item.passenger.audited).length;
    const noun = seatPassengers.length === 1 ? 'passenger' : 'passengers';
    const partial = this.seatPartialLegCount(seat) > 0 ? ' · not booked end to end' : '';
    return `${seatLabel} · ${seatPassengers.length} ${noun} · ${auditedCount}/${seatPassengers.length} audited${partial}`;
  }

  getPhotosForBusStop(busStopId: string) {
    const photos = this.photos();
    return photos.find((p: any) => p.busStopId === busStopId) ?? null;
  }

  getEntryPhotos(busStopId: string) {
    return this.getPhotosForBusStop(busStopId)?.entryPhotos ?? [];
  }

  getExitPhotos(busStopId: string) {
    return this.getPhotosForBusStop(busStopId)?.exitPhotos ?? [];
  }

  hasPhotos(busStopId: string) {
    const stop = this.getPhotosForBusStop(busStopId);
    return stop && (stop.entryPhotos.length > 0 || stop.exitPhotos.length > 0);
  }

  formatCurrency(value: number | null | undefined) {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return 'UGX 0';
    }

    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      currencyDisplay: 'code',
      maximumFractionDigits: 0,
    }).format(value);
  }

  trackBySeat = (_: number, seat: BookingSeatsDto) => seat.id;

  trackByStop = (_: number, stop: BusStopDto) => stop.id;

  trackByIndex = (index: number) => index;

  seatBlockClasses(seat: BookingSeatsDto) {
    const classes: Record<string, boolean> = {
      'seat-block': true,
    };

    if (seat.state === SeatBlockState.REMOVED) {
      classes['seat-block--spacer'] = true;
      return classes;
    }

    if (
      seat.state === SeatBlockState.DRIVER ||
      seat.state === SeatBlockState.CONDUCTOR ||
      seat.state === SeatBlockState.TURN_MAN
    ) {
      classes['seat-block--special'] = true;
      classes[`seat-block--special-${seat.state.toLowerCase()}`] = true;
      return classes;
    }

    classes['seat-block--passenger'] = true;

    const normalizedClass = seat.class?.toLowerCase?.() ?? '';
    if (normalizedClass === SeatClass.GOLD.toLowerCase()) {
      classes['seat-block--gold'] = true;
    } else if (normalizedClass === SeatClass.BRONZE.toLowerCase()) {
      classes['seat-block--bronze'] = true;
    } else {
      classes['seat-block--silver'] = true;
    }

    const occupancy = this.getSeatOccupancyStatus(seat);
    classes[`seat-block--${occupancy}`] = true;

    // Sold in pieces rather than end to end — drawn as diagonal bands, one per leg.
    const partialLegs = this.seatPartialLegCount(seat);
    if (partialLegs > 0) {
      classes['seat-block--partial'] = true;
      classes[`seat-block--partial-${Math.min(partialLegs, 4)}`] = true;
    }

    // Every commercial seat is clickable now (empty seats are auditable in seat-first flow).
    classes['seat-block--clickable'] = true;

    if (this.isSeatHighlighted(seat)) {
      classes['seat-block--active'] = true;
    }

    // "Done" covers fully-audited occupied seats AND confirmed-empty / anomaly empty seats.
    const seatDone = this.isSeatDone(seat);
    const seatPartiallyAudited = !seatDone && this.isSeatAudited(seat);

    if (seatDone) {
      classes['seat-block--fully-audited'] = true;
    } else if (seatPartiallyAudited) {
      classes['seat-block--audited'] = true;
    }

    if (this.hasUnbookedRiders(seat)) {
      classes['seat-block--anomaly'] = true;
    }

    return classes;
  }

  seatBlockLabel(seat: BookingSeatsDto) {
    if (seat.state === SeatBlockState.REMOVED) {
      return '';
    }

    if (
      seat.state === SeatBlockState.DRIVER ||
      seat.state === SeatBlockState.CONDUCTOR ||
      seat.state === SeatBlockState.TURN_MAN
    ) {
      return seat.label || seat.nickName || seat.state.substring(0, 2);
    }

    if (seat.state === SeatBlockState.PASSENGER_SEAT) {
      if (seat.nickName) {
        return seat.nickName;
      }
      if (seat.seatNumber) {
        return seat.seatNumber.toString();
      }
    }

    return '';
  }

  getSeatClassBadge(seatClass: string | null | undefined) {
    const normalized = seatClass?.toLowerCase?.() ?? '';

    if (normalized === SeatClass.GOLD.toLowerCase()) {
      return 'meta-pill--gold';
    }

    if (normalized === SeatClass.BRONZE.toLowerCase()) {
      return 'meta-pill--bronze';
    }

    return 'meta-pill--silver';
  }

  seatClassLabel(seatClass: string | null | undefined): string {
    const normalized = seatClass?.toLowerCase?.() ?? '';
    if (normalized === SeatClass.GOLD.toLowerCase()) {
      return 'Gold';
    }
    if (normalized === SeatClass.BRONZE.toLowerCase()) {
      return 'Bronze';
    }
    return 'Silver';
  }

  private estimateSeatColumns(totalSeats: number) {
    if (totalSeats <= 6) {
      return 2;
    }

    if (totalSeats <= 12) {
      return 3;
    }

    if (totalSeats <= 20) {
      return 4;
    }

    return 5;
  }

  private buildOperationView(operation: OperationsDto): AuditOperationViewModel {
    const busStops = [...(operation.route?.busStops ?? [])].sort((a, b) => a.orderIndex - b.orderIndex);
    const busStopById = new Map(busStops.map((stop) => [stop.id, stop] as const));
    const busStopByTerminalId = new Map(busStops.map((stop) => [stop.terminalId, stop] as const));
    const subRoutes = operation.route?.subRoutes ?? [];
    const subRouteById = new Map(subRoutes.map((route) => [route.id, route] as const));
    const subRouteByTerminals = new Map(
      subRoutes.map((route) => [`${route.fromTerminalId}::${route.toTerminalId}`, route] as const),
    );

    const seats = [...(operation.bookingProfile?.seats ?? [])].sort((a, b) => a.index - b.index);
    const seatById = new Map(seats.map((seat) => [seat.id, seat] as const));
    const seatByNumber = new Map(seats.map((seat) => [seat.seatNumber, seat] as const));
    const passengersSource = operation.bookingProfile?.passengersList ?? [];
    const passengerRecords = new Map<string, PassengerAuditViewModel>();

    const upsertPassenger = (passenger: SeatPassengerDto, candidateSeat: BookingSeatsDto | null = null) => {
      const auditable = this.isPassengerAuditable(passenger);
      if (!auditable) {
        this.logger.info('Passenger excluded from audit', {
          passengerId: passenger.id,
          status: passenger.status,
          isUsed: passenger.isUsed,
        });
        return;
      }

      const resolvedSeat =
        candidateSeat ??
        (passenger.bookingSeatId ? (seatById.get(passenger.bookingSeatId) ?? null) : null) ??
        (passenger.seatNumber != null ? (seatByNumber.get(passenger.seatNumber) ?? null) : null);

      const seatId = resolvedSeat?.id ?? passenger.bookingSeatId ?? null;
      const seatNumber = passenger.seatNumber ?? resolvedSeat?.seatNumber ?? Number.MAX_SAFE_INTEGER;

      const subRoute = subRouteById.get(passenger.subRouteId) ?? null;
      const originStop = subRoute ? (busStopByTerminalId.get(subRoute.fromTerminalId) ?? null) : null;
      const destinationStop = subRoute ? (busStopByTerminalId.get(subRoute.toTerminalId) ?? null) : null;
      const bookedPrice = subRoute ? this.getPriceForSeatClass(subRoute, passenger.seatClass) : 0;

      passengerRecords.set(passenger.id, {
        passenger,
        seat: resolvedSeat ?? null,
        seatId,
        seatNumber,
        originStop,
        destinationStop,
        bookedSubRoute: subRoute,
        bookedPrice,
      });
    };

    for (const passenger of passengersSource) {
      upsertPassenger(passenger);
    }

    for (const seat of seats) {
      for (const passenger of seat.passengers ?? []) {
        if (!passengerRecords.has(passenger.id)) {
          upsertPassenger(passenger, seat);
        }
      }
    }

    const passengers = Array.from(passengerRecords.values());

    this.logger.info('Audit passengers aggregation', {
      operationId: operation.id,
      sourceCount: passengersSource.length,
      seatCount: seats.length,
      mergedCount: passengers.length,
    });

    passengers.sort((a, b) => {
      // Primary: Sort by audited status (unaudited first)
      if (a.passenger.audited !== b.passenger.audited) {
        return a.passenger.audited ? 1 : -1;
      }

      // Secondary: Sort by booking time (earliest first)
      const aBookedAt = a.passenger.bookedAt ? new Date(a.passenger.bookedAt).getTime() : 0;
      const bBookedAt = b.passenger.bookedAt ? new Date(b.passenger.bookedAt).getTime() : 0;
      if (aBookedAt !== bBookedAt) {
        return aBookedAt - bBookedAt;
      }

      // Tertiary: Sort by seat number (ascending) as tiebreaker
      return a.seatNumber - b.seatNumber;
    });

    const passengersBySeatId = new Map<string, PassengerAuditViewModel[]>();
    const passengersBySeatNumber = new Map<number, PassengerAuditViewModel[]>();

    for (const passenger of passengers) {
      if (passenger.seatId) {
        const list = passengersBySeatId.get(passenger.seatId) ?? [];
        list.push(passenger);
        passengersBySeatId.set(passenger.seatId, list);
      }

      if (Number.isFinite(passenger.seatNumber) && passenger.seatNumber !== Number.MAX_SAFE_INTEGER) {
        const listByNumber = passengersBySeatNumber.get(passenger.seatNumber) ?? [];
        listByNumber.push(passenger);
        passengersBySeatNumber.set(passenger.seatNumber, listByNumber);
      }
    }

    return {
      raw: operation,
      seats,
      busStops,
      passengers,
      passengersBySeatId,
      passengersBySeatNumber,
      busStopById,
      subRouteByTerminals,
    };
  }

  private isPassengerAuditable(passenger: SeatPassengerDto) {
    if (!passenger) {
      return false;
    }

    const excludedStatuses = [PassengerStatusEnum.Cancelled, PassengerStatusEnum.Refund];
    const hasUsedTicket = passenger.isUsed ?? true;
    return hasUsedTicket && !excludedStatuses.includes(passenger.status as PassengerStatusEnum);
  }

  private getPriceForSeatClass(
    route: { goldPrice: number; silverPrice: number; bronzePrice: number },
    seatClass: string,
  ) {
    const normalized = seatClass?.toLowerCase?.() ?? '';

    if (normalized === SeatClass.GOLD.toLowerCase()) {
      return route.goldPrice;
    }

    if (normalized === SeatClass.BRONZE.toLowerCase()) {
      return route.bronzePrice;
    }

    return route.silverPrice;
  }

  private resolveAuditResult(
    passenger: PassengerAuditViewModel,
    dropStop: BusStopDto,
    fareDifference: FareDifferenceResult | null,
  ): CompletePassengerAuditDto['auditResult'] {
    const destinationOrder = passenger.destinationStop?.orderIndex ?? null;
    if (destinationOrder !== null && dropStop.orderIndex > destinationOrder) {
      return 'FAILED';
    }

    if (fareDifference && fareDifference.difference > 0) {
      return 'FAILED';
    }

    return 'PASSED';
  }

  // --- Empty-seat outcomes (persisted via operation_seat_audits) ---------------

  // Mark the active empty seat as reviewed & empty (counts toward seat progress), then
  // advance to the next seat needing attention.
  confirmEmptyActiveSeat() {
    const seat = this.activeSeat();
    const operationId = this.operationView()?.raw.id ?? this.operationId();
    // A seat with recorded riders is accounted-for, not empty — never confirm-empty it (the
    // button is hidden in that case, this is just a guard).
    if (
      !seat ||
      !operationId ||
      !this.activeSeatIsEmpty() ||
      this.unbookedRidersForActiveSeat().length > 0 ||
      this.confirmEmptySeatMutation.isPending()
    ) {
      return;
    }
    this.confirmEmptySeatMutation.mutate(
      { operationId, body: { busSeatId: seat.id, seatNumber: seat.seatNumber } },
      {
        onSuccess: () => {
          this.notification.success('✅ Seat Confirmed Empty', `Seat ${seat.seatNumber} marked reviewed & empty.`);
          this.navigateToNextNotDoneSeat();
        },
      },
    );
  }

  openRiderForm() {
    if (!this.activeSeat()) {
      return;
    }
    this.editingRiderId.set(null);
    this.riderOriginStopId.set(null);
    this.riderDropStopId.set(null);
    this.riderFormOpen.set(true);
  }

  // Open the form pre-filled to edit an existing recorded rider.
  editRider(rider: UnbookedRiderDraft) {
    if (!this.activeSeat()) {
      return;
    }
    this.editingRiderId.set(rider.id);
    this.riderOriginStopId.set(rider.auditedOriginStopId);
    this.riderDropStopId.set(rider.auditedDropStopId);
    this.riderFormOpen.set(true);
  }

  closeRiderForm() {
    this.riderFormOpen.set(false);
    this.editingRiderId.set(null);
  }

  // Forward-only option lists for the unbooked-rider form: boarding can only be a stop before the
  // chosen alighting, and alighting only a stop after the chosen boarding.
  riderBoardingOptions = computed(() => {
    const stops = this.busStops();
    const dropIdx = this.stopOrderIndex(this.riderDropStopId());
    return dropIdx === null ? stops : stops.filter((s) => s.orderIndex < dropIdx);
  });

  riderAlightingOptions = computed(() => {
    const stops = this.busStops();
    const boardIdx = this.stopOrderIndex(this.riderOriginStopId());
    return boardIdx === null ? stops : stops.filter((s) => s.orderIndex > boardIdx);
  });

  canRecordRider() {
    const board = this.stopOrderIndex(this.riderOriginStopId());
    const alight = this.stopOrderIndex(this.riderDropStopId());
    return !!this.activeSeat() && board !== null && alight !== null && board < alight;
  }

  // Record (or update) an unbooked rider seen on an "empty" seat — persisted to
  // unbooked_rider_audits, then re-hydrated from the server.
  recordUnbookedRider() {
    const view = this.operationView();
    const seat = this.activeSeat();
    const operationId = view?.raw.id ?? this.operationId();
    const originStopId = this.riderOriginStopId();
    const dropStopId = this.riderDropStopId();
    if (!view || !seat || !operationId || !originStopId || !dropStopId || this.saveUnbookedRiderMutation.isPending()) {
      return;
    }
    const originStop = view.busStopById.get(originStopId);
    const dropStop = view.busStopById.get(dropStopId);
    if (!originStop || !dropStop) {
      return;
    }
    if (originStop.orderIndex >= dropStop.orderIndex) {
      this.notification.warning('⚠️ Invalid Stops', 'Boarding must come before alighting on the route.');
      return;
    }

    const bookingProfileId = view.raw.bookingProfile?.id;
    if (!bookingProfileId) {
      this.notification.error(
        '❌ Missing Operation Data',
        'The booking profile could not be identified. Please reload and try again.',
      );
      return;
    }

    const estimate = this.estimateEvadedFare(seat, originStopId, dropStopId, view);
    const editingId = this.editingRiderId();
    const isUpdate = !!editingId;

    const body: RecordUnbookedRiderDto = {
      bookingProfileId,
      busSeatId: seat.id,
      seatNumber: seat.seatNumber,
      seatClass: seat.class ?? undefined,
      channel: this.activeSeatTag()?.channel ?? undefined,
      auditedSubRouteId: estimate?.actualSubRoute?.id ?? undefined,
      auditedSubRouteName: estimate?.actualSubRoute?.name ?? undefined,
      auditedOriginStopId: originStop.id,
      auditedOriginStopName: originStop.name,
      auditedOriginOrderIndex: originStop.orderIndex,
      auditedDropStopId: dropStop.id,
      auditedDropStopName: dropStop.name,
      auditedDropOrderIndex: dropStop.orderIndex,
      evadedFare: estimate?.evadedFare ?? 0,
      detectedAt: new Date().toISOString(),
    };

    this.saveUnbookedRiderMutation.mutate(
      { operationId, riderId: editingId, body },
      {
        onSuccess: () => {
          this.notification.success(
            isUpdate ? '✅ Unbooked Rider Updated' : '✅ Unbooked Rider Recorded',
            `Seat ${seat.seatNumber}: ${this.formatCurrency(body.evadedFare)} evaded recorded.`,
          );
          this.riderFormOpen.set(false);
          this.editingRiderId.set(null);
        },
      },
    );
  }

  // Reversal: drop a previously-recorded unbooked rider from the active seat. If it was the seat's
  // only anomaly, the seat reverts to "not done" until the auditor confirms empty or adds another.
  removeUnbookedRider(rider: UnbookedRiderDraft) {
    const seat = this.activeSeat();
    const operationId = this.operationView()?.raw.id ?? this.operationId();
    if (!seat || !operationId || this.deleteUnbookedRiderMutation.isPending()) {
      return;
    }
    this.deleteUnbookedRiderMutation.mutate(
      { operationId, riderId: rider.id },
      {
        onSuccess: () => {
          this.notification.info(
            'Unbooked Rider Removed',
            `Seat ${seat.seatNumber}: removed ${rider.auditedOriginStopName} → ${rider.auditedDropStopName}.`,
          );
        },
      },
    );
  }

  // --- Persisted audit-records hydration ---------------------------------------

  // Re-fetch the persisted unbooked riders + confirmed-empty seats after a write so the
  // seat panel reflects the server (the syncAuditRecords effect re-runs on new data).
  private refreshAuditRecords() {
    this.auditRecordsQuery.refetch();
  }

  // Rebuild `unbookedRidersBySeat` + `confirmedEmptySeats` from the persisted records.
  private hydrateAuditRecords(records: OperationAuditRecordsDto, view: AuditOperationViewModel | null) {
    const bySeat = new Map<string, UnbookedRiderDraft[]>();
    for (const r of records.unbookedRiders) {
      const seatId = r.busSeatId ?? this.resolveSeatIdByNumber(r.seatNumber, view);
      if (!seatId) {
        continue;
      }
      const draft: UnbookedRiderDraft = {
        id: r.id,
        seatId,
        seatNumber: r.seatNumber,
        channel: r.channel,
        auditedOriginStopId: r.auditedOriginStopId,
        auditedOriginStopName: r.auditedOriginStopName,
        auditedOriginOrderIndex: r.auditedOriginOrderIndex,
        auditedDropStopId: r.auditedDropStopId,
        auditedDropStopName: r.auditedDropStopName,
        auditedDropStopOrderIndex: r.auditedDropOrderIndex,
        evadedFare: r.evadedFare,
        actualSubRouteId: r.auditedSubRouteId,
        detectedAt: r.detectedAt,
      };
      const list = bySeat.get(seatId) ?? [];
      list.push(draft);
      bySeat.set(seatId, list);
    }
    this.unbookedRidersBySeat.set(bySeat);

    const emptySet = new Set<string>();
    for (const s of records.confirmedEmptySeats) {
      const seatId = s.busSeatId ?? this.resolveSeatIdByNumber(s.seatNumber, view);
      if (seatId) {
        emptySet.add(seatId);
      }
    }
    this.confirmedEmptySeats.set(emptySet);
  }

  private resolveSeatIdByNumber(seatNumber: number, view: AuditOperationViewModel | null): string | null {
    if (!view) {
      return null;
    }
    return view.seats.find((s) => s.seatNumber === seatNumber)?.id ?? null;
  }

  // Evaded fare for an unbooked rider: there is no booking, so the whole audited-segment
  // price for the seat's class is the loss. Reuses the same sub-route + pricing machinery
  // as calculateFareDifference.
  private estimateEvadedFare(
    seat: BookingSeatsDto,
    originStopId: string,
    dropStopId: string,
    view: AuditOperationViewModel,
  ): { evadedFare: number; actualSubRoute: SubRouteDto | null } | null {
    const originStop = view.busStopById.get(originStopId) ?? null;
    const dropStop = view.busStopById.get(dropStopId) ?? null;
    if (!originStop || !dropStop) {
      return null;
    }
    const key = `${originStop.terminalId}::${dropStop.terminalId}`;
    const actualSubRoute = view.subRouteByTerminals.get(key) ?? null;
    const seatClass = seat.class ?? SeatClass.SILVER;
    const evadedFare = actualSubRoute ? this.getPriceForSeatClass(actualSubRoute, seatClass) : 0;
    return { evadedFare, actualSubRoute };
  }

  private calculateFareDifference(
    passenger: PassengerAuditViewModel,
    auditedOriginStopId: string,
    auditedDropStopId: string,
    view: AuditOperationViewModel,
  ): FareDifferenceResult | null {
    const auditedOriginStop = view.busStopById.get(auditedOriginStopId) ?? null;
    const auditedDropStop = view.busStopById.get(auditedDropStopId) ?? null;
    if (!auditedOriginStop || !auditedDropStop) return null;

    const key = `${auditedOriginStop.terminalId}::${auditedDropStop.terminalId}`;
    const actualSubRoute = view.subRouteByTerminals.get(key) ?? null;

    const originalPrice = passenger.bookedPrice;
    const auditedPrice = actualSubRoute
      ? this.getPriceForSeatClass(actualSubRoute, passenger.passenger.seatClass)
      : originalPrice;

    return {
      originalPrice,
      auditedPrice,
      difference: auditedPrice - originalPrice,
      actualSubRoute,
      actualStop: auditedDropStop,
    };
  }
}

export interface PassengerAuditViewModel {
  passenger: SeatPassengerDto;
  seat: BookingSeatsDto | null;
  seatId: string | null;
  seatNumber: number;
  originStop: BusStopDto | null;
  destinationStop: BusStopDto | null;
  bookedSubRoute: SubRouteDto | null;
  bookedPrice: number;
}

interface AuditOperationViewModel {
  raw: OperationsDto;
  seats: BookingSeatsDto[];
  busStops: BusStopDto[];
  passengers: PassengerAuditViewModel[];
  passengersBySeatId: Map<string, PassengerAuditViewModel[]>;
  passengersBySeatNumber: Map<number, PassengerAuditViewModel[]>;
  busStopById: Map<string, BusStopDto>;
  subRouteByTerminals: Map<string, SubRouteDto>;
}

interface FareDifferenceResult {
  originalPrice: number;
  auditedPrice: number;
  difference: number;
  actualSubRoute: SubRouteDto | null;
  actualStop: BusStopDto | null;
}

interface CompletePassengerAuditMutationPayload {
  passengerId: string;
  body: CompletePassengerAuditDto;
  // True when this save is a correction to an already-audited passenger (vs a first audit).
  wasAlreadyAudited: boolean;
  // Whether to auto-advance to the next un-audited passenger on success. Save advances on a
  // first audit; mark-as-start/end instant-persists never advance (the auditor stays put).
  advance: boolean;
}

// View model for an unbooked rider seen on an "empty" seat. Persisted in `unbooked_rider_audits`
// (id is the server row id; the list is hydrated from GET .../records on load).
interface UnbookedRiderDraft {
  id: string; // server row id (used for edit/remove + stable tracking)
  seatId: string;
  seatNumber: number;
  channel: number | null;
  auditedOriginStopId: string;
  auditedOriginStopName: string;
  auditedOriginOrderIndex: number;
  auditedDropStopId: string;
  auditedDropStopName: string;
  auditedDropStopOrderIndex: number;
  evadedFare: number;
  actualSubRouteId: string | null;
  detectedAt: string;
}
